<script lang="ts">
  /** Keyboard shortcut sheet (? key), generated from the live keymap so rebinds show up. */
  import { ACTIONS, LEFT_KEYS, describeBinding, prettyChord } from '../../core/actions';
  import type { AppController } from '../../app/controller.svelte';
  import Icon from './Icon.svelte';

  let { ctl }: { ctl: AppController } = $props();

  const pretty = prettyChord;

  /** The keyboard, row by row, as the keymap's key names; `w` is a width in key units. */
  const ROWS: { key: string; cap?: string; w?: number }[][] = [
    [...'`1234567890-='.split('').map((key) => ({ key })), { key: 'Backspace', cap: '⌫', w: 1.5 }],
    [{ key: 'Tab', cap: 'Tab', w: 1.5 }, ...'QWERTYUIOP[]\\'.split('').map((key) => ({ key }))],
    [
      { key: 'Caps', cap: '', w: 1.8 },
      ..."ASDFGHJKL;'".split('').map((key) => ({ key })),
      { key: 'Enter', cap: '↩', w: 1.8 },
    ],
    [
      { key: 'Shift', cap: '⇧', w: 2.3 },
      ...'ZXCVBNM,./'.split('').map((key) => ({ key })),
      { key: 'Shift2', cap: '⇧', w: 2.3 },
    ],
    [{ key: 'Space', cap: 'Space', w: 6 }],
  ];
  const short = (chord: string): string => {
    const b = ctl.keymap[chord];
    if (!b) return '';
    const def = ACTIONS.find((a) => a.id === b.action);
    if (!def) return '';
    return def.slot ? `${def.short} ${(b.arg ?? 0) === 9 ? 0 : (b.arg ?? 0) + 1}` : def.short;
  };
  const zone = (key: string) =>
    LEFT_KEYS.includes(key) ? 'left' : ['Caps', 'Shift', 'Shift2'].includes(key) ? 'mod' : 'right';

  /** Slot actions collapse to one row ("Recall preset in slot  1–0"). */
  const groups = $derived.by(() => {
    const out: { category: string; rows: { label: string; chords: string[] }[] }[] = [];
    for (const a of ACTIONS) {
      const entries = Object.entries(ctl.keymap).filter(([, b]) => b.action === a.id);
      if (entries.length === 0) continue;
      let group = out.find((g) => g.category === a.category);
      if (!group) out.push((group = { category: a.category, rows: [] }));
      if (a.slot) {
        const mods = new Set(entries.map(([c]) => c.replace(/[0-9]$/, '')));
        group.rows.push({ label: a.label, chords: [...mods].map((m) => `${pretty(m)}1–0`) });
      } else {
        group.rows.push({
          label: describeBinding({ action: a.id }),
          chords: entries.map(([c]) => pretty(c)),
        });
      }
    }
    return out;
  });
</script>

<div
  class="backdrop"
  role="presentation"
  onclick={(e) => {
    if (e.target === e.currentTarget) ctl.helpOpen = false;
  }}
>
  <div
    class="sheet"
    role="dialog"
    aria-modal="true"
    aria-labelledby="help-title"
    data-testid="help-overlay"
  >
    <div class="sec-head">
      <h2 id="help-title">Help</h2>
      <button class="icon-btn" aria-label="Close" onclick={() => (ctl.helpOpen = false)}
        ><Icon name="close" /></button
      >
    </div>
    <div class="tour-row">
      <p class="small muted">
        New here, or want a refresher? The tour walks through every feature.
      </p>
      <button class="primary" data-testid="restart-tour" onclick={() => ctl.startTour()}
        >Take the tour</button
      >
    </div>
    <h3 class="keys-title">Keyboard shortcuts</h3>
    <p class="small muted zones-hint">
      Split like a game: <span class="sw left"></span> the left hand plays the show (presets,
      favorites, beat, take), <span class="sw right"></span> the right hand or the mouse shapes the look
      (the selected layer and palette) and opens windows. ⇧ is the opposite or bigger move.
    </p>
    <div class="kb" data-testid="keyboard-map" aria-hidden="true">
      {#each ROWS as row, r (r)}
        <div class="kb-row">
          {#each row as k (k.key)}
            <div class="key {zone(k.key)}" style:flex-grow={k.w ?? 1}>
              <span class="cap">{k.cap ?? k.key}</span>
              <span class="act">{short(k.key)}</span>
              {#if short(`Shift+${k.key}`)}<span class="act shift">⇧ {short(`Shift+${k.key}`)}</span
                >{/if}
            </div>
          {/each}
        </div>
      {/each}
    </div>
    <div class="cols">
      {#each groups as g (g.category)}
        <section>
          <h3>{g.category}</h3>
          <dl>
            {#each g.rows as r (r.label)}
              <dt>{r.label}</dt>
              <dd>
                {#each r.chords as c (c)}<kbd>{c}</kbd>{/each}
              </dd>
            {/each}
          </dl>
        </section>
      {/each}
    </div>
    <p class="hint">
      Rebind keys in Settings → Keyboard shortcuts. In performance mode, press Esc twice to leave.
    </p>
  </div>
</div>

<style>
  .tour-row {
    display: flex;
    align-items: center;
    gap: 12px;
    justify-content: space-between;
    padding: 10px 12px;
    margin-bottom: 14px;
    border-radius: var(--radius-sm);
    background: rgba(255, 255, 255, 0.04);
  }
  .tour-row p {
    margin: 0;
  }
  .keys-title {
    margin: 0 0 6px;
    font-size: 13px;
  }
  .zones-hint {
    margin: 0 0 10px;
  }
  .sw {
    display: inline-block;
    width: 10px;
    height: 10px;
    border-radius: 3px;
    vertical-align: -1px;
  }
  .sw.left,
  .key.left {
    background: color-mix(in srgb, var(--accent) 16%, transparent);
  }
  .sw.right,
  .key.right {
    background: color-mix(in srgb, #7aa2ff 14%, transparent);
  }
  .kb {
    display: flex;
    flex-direction: column;
    gap: 3px;
    margin-bottom: 16px;
    overflow-x: auto;
    min-width: 0;
  }
  .kb-row {
    display: flex;
    gap: 3px;
    min-width: 640px;
  }
  .kb-row:last-child {
    justify-content: center;
  }
  .key {
    flex: 1 1 0;
    min-height: 40px;
    padding: 3px 4px;
    border-radius: 5px;
    border: 1px solid var(--border);
    display: flex;
    flex-direction: column;
    gap: 1px;
    overflow: hidden;
  }
  .kb-row:last-child .key {
    flex: 0 0 40%;
  }
  .key.mod {
    background: rgba(255, 255, 255, 0.03);
  }
  .cap {
    font-family: var(--mono);
    font-size: 11px;
    color: var(--muted);
  }
  .act {
    font-size: 9.5px;
    line-height: 1.15;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .act.shift {
    color: var(--faint);
  }
  .backdrop {
    position: absolute;
    inset: 0;
    z-index: 35;
    display: grid;
    place-items: center;
    padding: 16px;
    background: rgba(5, 5, 8, 0.6);
  }
  .sheet {
    width: min(860px, 100%);
    max-height: calc(100vh - 32px);
    overflow: auto;
    padding: 16px 20px;
    border-radius: var(--radius);
    border: 1px solid var(--border);
    background: var(--panel-solid);
  }
  .cols {
    columns: 3 220px;
    column-gap: 28px;
  }
  section {
    break-inside: avoid;
    margin-bottom: 14px;
  }
  h3 {
    margin: 0 0 6px;
    font-size: 11.5px;
    text-transform: uppercase;
    letter-spacing: 0.08em;
    color: var(--muted);
  }
  dl {
    display: grid;
    grid-template-columns: 1fr auto;
    gap: 4px 12px;
    margin: 0;
    font-size: 13px;
  }
  dd {
    margin: 0;
    display: flex;
    gap: 4px;
    justify-content: flex-end;
  }
  kbd {
    font-family: var(--mono);
    font-size: 11.5px;
    padding: 1px 6px;
    border-radius: 5px;
    border: 1px solid var(--border);
    background: rgba(255, 255, 255, 0.05);
    white-space: nowrap;
  }
</style>
